/**
 * Theme: Shopify Dawn Compatible
 * JavaScript: Header Section Interactions & Behaviors
 * Project: Wearjags Premium
 * Author: Iterative Lab
 * Version: 1.0.0
 */

class HeaderSection extends HTMLElement {
  constructor() {
    super();
    this.header = this;
    this.stickyEnabled = this.classList.contains('wj-header-sticky');
    
    // DOM Elements
    this.announcementSlider = this.querySelector('.wj-announcement-slider');
    this.mobileDrawerToggle = this.querySelector('.wj-drawer-toggle');
    this.mobileDrawerClose = this.querySelector('.wj-drawer-close');
    this.mobileDrawer = this.querySelector('#wj-mobile-menu');
    this.drawerMask = this.querySelector('#wj-drawer-mask');
    this.localizationTrigger = this.querySelector('.wj-selector-trigger');
    this.localizationPopup = this.querySelector('#wj-localization-popup');
    this.menuItems = this.querySelectorAll('.wj-menu-item.wj-has-megamenu');
    this.megaMenuPanel = this.querySelector('#wj-megamenu-panel');
    this.megaMenuGrid = this.querySelector('#wj-megamenu-grid-content');

    // State
    this.scrollThreshold = 20;
    this.activeMegaMenu = null;

    // Mega menu content dictionary (simulate Shopify structure)
    this.megaMenuData = {
      new: {
        cols: [
          {
            title: "New Arrivals",
            links: [
              { title: "Everything New", url: "/collections/new" },
              { title: "Traject Prime Outerwear", url: "/collections/outerwear" },
              { title: "Compression Tech Shorts", url: "/collections/shorts" },
              { title: "VaporLite Seamless Ribbed", url: "/collections/seamless" }
            ]
          },
          {
            title: "The Performance Drop",
            links: [
              { title: "All Performance Tops", url: "/collections/tops" },
              { title: "Compression Vests & Bras", url: "/collections/bras" },
              { title: "Luxe Athleisure Sets", url: "/collections/sets" },
              { title: "Thermal Jogger Sweats", url: "/collections/joggers" }
            ]
          },
          {
            title: "Color Spotlight",
            links: [
              { title: "Desert Clay Sand", url: "/collections/clay-sand" },
              { title: "Obsidian Core Black", url: "/collections/obsidian" },
              { title: "Polar Ice Blue", url: "/collections/ice-blue" },
              { title: "Sage Horizon Green", url: "/collections/sage" }
            ]
          }
        ]
      },
      clothing: {
        cols: [
          {
            title: "Bottoms",
            links: [
              { title: "Laminated Sculpt Leggings", url: "/collections/leggings" },
              { title: "FlexTech Athletic Joggers", url: "/collections/joggers" },
              { title: "Biolite Lightweight Shorts", url: "/collections/shorts" },
              { title: "Aeroflex Split Skirts", url: "/collections/skirts" }
            ]
          },
          {
            title: "Tops & Layers",
            links: [
              { title: "Compressive Impact Bras", url: "/collections/bras" },
              { title: "Seamless Cropped Tanks", url: "/collections/cropped-tanks" },
              { title: "AeroRib Long Sleeve Tops", url: "/collections/long-sleeves" },
              { title: "DryForm Technical Tees", url: "/collections/technical-tees" }
            ]
          },
          {
            title: "Outerwear & Hoodies",
            links: [
              { title: "Thermoregulating Jackets", url: "/collections/jackets" },
              { title: "UltraStretch Zip Hoodies", url: "/collections/hoodies" },
              { title: "Windbreak Shell Pullovers", url: "/collections/pullovers" }
            ]
          }
        ]
      }
    };
  }

  connectedCallback() {
    this.initStickyScroll();
    this.initAnnouncements();
    this.initMobileDrawer();
    this.initMegaMenus();
    this.initLocalization();
    this.initSearchOverlay();
  }

  // Search interaction drawer popup overlay
  initSearchOverlay() {
    const searchTrigger = this.querySelector('#wj-search-trigger');
    const searchOverlay = this.querySelector('#wj-search-overlay');
    const searchClose = this.querySelector('#wj-search-close-btn');
    const searchInput = this.querySelector('#wj-search-input-box');
    const clearBtn = this.querySelector('#wj-search-clear-btn');
    const tagPills = this.querySelectorAll('.wj-search-tag-pill');

    if (searchTrigger && searchOverlay) {
      searchTrigger.addEventListener('click', (e) => {
        searchOverlay.classList.add('is-active');
        document.body.style.overflow = 'hidden'; // Stop background scroll
        setTimeout(() => { if (searchInput) searchInput.focus(); }, 150);
        e.stopPropagation();
      });
    }

    const closeOverlay = () => {
      if (searchOverlay) {
        searchOverlay.classList.remove('is-active');
        document.body.style.overflow = ''; // Restore background scroll
      }
    };

    if (searchClose) {
      searchClose.addEventListener('click', closeOverlay);
    }

    if (searchOverlay) {
      searchOverlay.addEventListener('click', (e) => {
        // Close if click is outside modal inner (directly on overlay wrapper)
        if (e.target === searchOverlay) {
          closeOverlay();
        }
      });
    }

    // Toggle Clear button and has-input class
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        const hasText = searchInput.value.trim().length > 0;
        if (hasText) {
          searchOverlay.classList.add('has-input');
          if (clearBtn) clearBtn.classList.remove('hidden');
        } else {
          searchOverlay.classList.remove('has-input');
          if (clearBtn) clearBtn.classList.add('hidden');
        }
      });
    }

    if (clearBtn && searchInput) {
      clearBtn.addEventListener('click', () => {
        searchInput.value = '';
        searchOverlay.classList.remove('has-input');
        clearBtn.classList.add('hidden');
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        searchInput.focus();
      });
    }

    // Tag Pill Search Triggers
    if (tagPills && searchInput) {
      tagPills.forEach(pill => {
        pill.addEventListener('click', () => {
          searchInput.value = pill.getAttribute('data-tag');
          searchOverlay.classList.add('has-input');
          if (clearBtn) clearBtn.classList.remove('hidden');
          // Dispatch input event to notify predictive-search.js
          searchInput.dispatchEvent(new Event('input', { bubbles: true }));
          searchInput.dispatchEvent(new Event('change', { bubbles: true }));
          searchInput.focus();
        });
      });
    }
  }

  // 1. STICKY HEADER INTERACTION MANAGER
  initStickyScroll() {
    if (!this.stickyEnabled) return;
    
    let lastScrollTop = 0;
    const scrollThreshold = this.scrollThreshold || 20;
    let ticking = false;

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (scrollTop > scrollThreshold) {
            if (!this.classList.contains('is-scrolled')) {
              this.classList.add('is-scrolled');
            }
            
            // Hide announcements & utilities, compress header when scrolling down
            if (scrollTop > lastScrollTop) {
              if (!this.classList.contains('header--compact')) {
                this.classList.add('header--compact');
              }
            } else {
              // Show on scroll up
              if (this.classList.contains('header--compact')) {
                this.classList.remove('header--compact');
              }
            }
          } else {
            if (this.classList.contains('is-scrolled')) {
              this.classList.remove('is-scrolled');
            }
            if (this.classList.contains('header--compact')) {
              this.classList.remove('header--compact');
            }
          }
          
          // Prevent negative values from iOS bounce scroll behaviors
          lastScrollTop = Math.max(0, scrollTop);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial load check
  }

  // 2. MULTIPLE ANNOUNCEMENT ROTATOR
  initAnnouncements() {
    if (!this.announcementSlider) return;

    const autoplay = this.announcementSlider.dataset.autoplay === 'true';
    const speed = parseInt(this.announcementSlider.dataset.speed, 10) || 5000;
    const items = this.announcementSlider.querySelectorAll('.wj-announcement-item');
    
    if (items.length <= 1) return;

    let currentIndex = 0;
    let rotationInterval;

    const nextAnnouncement = () => {
      items[currentIndex].classList.remove('is-active');
      currentIndex = (currentIndex + 1) % items.length;
      items[currentIndex].classList.add('is-active');
    };

    if (autoplay) {
      rotationInterval = setInterval(nextAnnouncement, speed);
      
      // Pause on hover is premium UX
      this.announcementSlider.addEventListener('mouseenter', () => clearInterval(rotationInterval));
      this.announcementSlider.addEventListener('mouseleave', () => {
        rotationInterval = setInterval(nextAnnouncement, speed);
      });
    }
  }

  // 3. MOBILE MENU SLIDE OUT & TAP SUBNAV EXPANSIONS
  initMobileDrawer() {
    if (!this.mobileDrawerToggle) return;

    // Toggle Mobile Drawer
    const openDrawer = () => {
      this.mobileDrawerToggle.setAttribute('aria-expanded', 'true');
      if (this.mobileDrawer) this.mobileDrawer.classList.add('is-active');
      if (this.drawerMask) this.drawerMask.classList.add('is-active');
      document.body.style.overflow = 'hidden'; // Stop scroll leak
    };

    const closeDrawer = () => {
      this.mobileDrawerToggle.setAttribute('aria-expanded', 'false');
      if (this.mobileDrawer) this.mobileDrawer.classList.remove('is-active');
      if (this.drawerMask) this.drawerMask.classList.remove('is-active');
      document.body.style.overflow = '';
    };

    this.mobileDrawerToggle.addEventListener('click', openDrawer);
    if (this.mobileDrawerClose) this.mobileDrawerClose.addEventListener('click', closeDrawer);
    if (this.drawerMask) this.drawerMask.addEventListener('click', closeDrawer);

    // Expand / Collapse submenus inside the drawer with safe clicks on expand button
    const expandButtons = this.querySelectorAll('.wj-drawer-expand');
    expandButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const item = btn.closest('.wj-drawer-item');
        if (item) item.classList.toggle('is-expanded');
      });
    });

    // Handle clicks on actual text links to expand categories on mobile when sublink lists exist
    const drawerLinks = this.querySelectorAll('.wj-drawer-link');
    drawerLinks.forEach(link => {
      const item = link.closest('.wj-drawer-item');
      if (item && item.querySelector('.wj-drawer-expand')) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          item.classList.toggle('is-expanded');
        });
      }
    });

    // Tap on rows of list items containing sublinks to toggle expansion
    const drawerRows = this.querySelectorAll('.wj-drawer-link-row');
    drawerRows.forEach(row => {
      const item = row.closest('.wj-drawer-item');
      if (item && item.querySelector('.wj-drawer-expand')) {
        row.style.cursor = 'pointer';
        row.addEventListener('click', (e) => {
          if (!e.target.closest('.wj-drawer-expand') && !e.target.closest('.wj-drawer-link')) {
            item.classList.toggle('is-expanded');
          }
        });
      }
    });
  }

  // 4. MEGA MENU HIGHLIGHTS (Shopify compliant triggers)
  initMegaMenus() {
    if (!this.megaMenuPanel) return;

    // Load dynamic Shopify links parsed from Liquid data source
    const menuDataEl = this.querySelector('#wj-menu-data');
    if (menuDataEl) {
      try {
        const dynamicMenus = JSON.parse(menuDataEl.textContent);
        this.megaMenuData = { ...this.megaMenuData, ...dynamicMenus };
      } catch (e) {
        console.error("Failed to parse dynamic Shopify menu list:", e);
      }
    }

    let closeTimeout = null;

    const showMegaMenu = (handle) => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
      }

      const data = this.megaMenuData[handle];
      if (!data || !data.cols) return;

      this.activeMegaMenu = handle;

      // Clean existing render inside grid layout
      this.megaMenuGrid.innerHTML = '';
      
      // Populate Columns
      data.cols.forEach(col => {
        if (!col) return;
        const colEl = document.createElement('div');
        colEl.className = 'wj-megamenu-column';
        
        const titleEl = document.createElement('h3');
        titleEl.className = 'wj-megamenu-col-title';
        titleEl.innerText = col.title;
        colEl.appendChild(titleEl);

        const linksList = document.createElement('ul');
        linksList.className = 'wj-megamenu-links';

        const childLinks = col.links || [];
        childLinks.forEach(l => {
          const lItem = document.createElement('li');
          const aEl = document.createElement('a');
          aEl.href = typeof l === 'string' ? '#' : l.url || '#';
          aEl.className = 'wj-megamenu-link-item';
          aEl.innerText = typeof l === 'string' ? l : l.title;
          lItem.appendChild(aEl);
          linksList.appendChild(lItem);
        });

        colEl.appendChild(linksList);
        this.megaMenuGrid.appendChild(colEl);
      });

      // Show/Hide matching Promotional spotlights
      const promos = this.querySelectorAll('.wj-promo-card');
      promos.forEach(p => {
        if (p.dataset.trigger === handle) {
          p.classList.add('is-visible');
        } else {
          p.classList.remove('is-visible');
        }
      });

      // Slide and display panel
      this.megaMenuPanel.classList.add('is-active');
    };

    const hideMegaMenu = () => {
      this.megaMenuPanel.classList.remove('is-active');
      this.activeMegaMenu = null;
    };

    const scheduleHide = () => {
      if (closeTimeout) clearTimeout(closeTimeout);
      closeTimeout = setTimeout(() => {
        hideMegaMenu();
      }, 200); // Premium UX delay
    };

    const cancelHide = () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
      }
    };

    // Grab all menu items to manage triggers cleanly
    const parentMenuLinks = this.querySelectorAll('.wj-menu-item');
    parentMenuLinks.forEach(item => {
      const handle = item.dataset.handle;
      const isMega = item.classList.contains('wj-has-megamenu');
      
      item.addEventListener('mouseenter', () => {
        cancelHide();
        if (isMega && this.megaMenuData[handle]) {
          showMegaMenu(handle);
        } else {
          // Instantly close or schedule closed if moving off list
          hideMegaMenu();
        }
      });

      item.addEventListener('mouseleave', () => {
        if (isMega) {
          scheduleHide();
        }
      });
    });

    // Also hold active menu open if hovering is inside megamenu bounds
    if (this.megaMenuPanel) {
      this.megaMenuPanel.addEventListener('mouseenter', () => {
        cancelHide();
      });

      this.megaMenuPanel.addEventListener('mouseleave', () => {
        scheduleHide();
      });
    }

    // Dismiss on clicking away anywhere else in the document
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        hideMegaMenu();
      }
    });
  }

  // 5. LOCALIZATION DROPDOWNS
  initLocalization() {
    if (!this.localizationTrigger) return;

    this.localizationTrigger.addEventListener('click', (e) => {
      const isExpanded = this.localizationTrigger.getAttribute('aria-expanded') === 'true';
      this.localizationTrigger.setAttribute('aria-expanded', !isExpanded);
      this.localizationPopup.classList.toggle('is-active');
      e.stopPropagation();
    });

    // Close on outer click
    document.addEventListener('click', (e) => {
      if (!this.localizationTrigger.contains(e.target)) {
        this.localizationTrigger.setAttribute('aria-expanded', 'false');
        if (this.localizationPopup) this.localizationPopup.classList.remove('is-active');
      }
    });

    // Dropdown list click simulation
    const links = this.querySelectorAll('.wj-localization-list li a');
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        links.forEach(l => l.classList.remove('is-selected'));
        link.classList.add('is-selected');
        
        // Update label
        const label = this.querySelector('.wj-selected-localization');
        if (label) {
          label.innerHTML = link.textContent;
        }
        
        this.localizationTrigger.setAttribute('aria-expanded', 'false');
        this.localizationPopup.classList.remove('is-active');
      });
    });
  }
}

// Define the custom element
if (!customElements.get('header-section')) {
  customElements.define('header-section', HeaderSection);
}
